import React, { useEffect, useState } from 'react';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { defineMessages, useIntl } from 'react-intl';
import {
  COLOR_DENIM,
  COLOR_GOLDEN_TAINOI,
  COLOR_KOBI,
  COLOR_MONTE_CARLO,
  COLOR_SAIL,
  COLOR_SALMON,
  FONT_WEIGHT_NORMAL,
  FONT_SIZE_39_PX,
  COLOR_REGENT_ST_BLUE,
} from '../../popTheme';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectResults } from '../../redux/reducers/results';
import { ResultsGraphSection } from './ResultsGraphSection';
import { ResultsCardsSection } from './ResultsCardsSection';
import { sendGetResourceData } from 'redux/sagas/getResourceDataSaga';
import { selectResourceData } from 'redux/reducers/resourceDataReducer';
import {
  CATEGORY,
  CategorySection,
  CategorySectionList,
  getCategoryResources,
} from 'components/home/ResourceCategory';
import { sendGetQuestionnaireResponses } from 'redux/sagas/questionnaireResponseSaga';
import AssessmentActions from 'components/checkIn/questionnaire/AssessmentActions';
import { selectStudyCode } from 'redux/reducers/session';

export const ResultCategories = {
  PAIN: 'pain',
  MOOD: 'mood',
  SLEEP: 'sleep',
  WORRY: 'worry',
  SUBSTANCE: 'substance',
  NOTES: 'notes',
};

export const convertResultCategoryToResourceCategory = (resultCategory) => {
  switch (resultCategory) {
    case ResultCategories.PAIN:
      return CATEGORY.pain;
    case ResultCategories.WORRY:
      return CATEGORY.worry;
    case ResultCategories.MOOD:
      return CATEGORY.mood;
    case ResultCategories.SLEEP:
      return CATEGORY.sleep;
    case ResultCategories.SUBSTANCE:
      return CATEGORY.substanceUse;
    default:
      return null;
  }
};

export const getColorForQuestionnaireType = (questionnaireType) => {
  switch (questionnaireType) {
    case ResultCategories.PAIN:
      return COLOR_SAIL;
    case ResultCategories.WORRY:
      return COLOR_SALMON;
    case ResultCategories.MOOD:
      return COLOR_GOLDEN_TAINOI;
    case ResultCategories.SLEEP:
      return COLOR_KOBI;
    case ResultCategories.SUBSTANCE:
      return COLOR_MONTE_CARLO;
    case ResultCategories.NOTES:
      return COLOR_REGENT_ST_BLUE;
    default:
      return null;
  }
};

// The only way to be able to test the XYChart is to allow the tests to provide a width: https://github.com/airbnb/visx/issues/951#issuecomment-785305999
export const Results = ({ chartWidth }) => {
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // We need to get the resource data for the recommended section and the questionnaire responses for the actual results.
  useEffect(() => {
    dispatch(sendGetResourceData());
    dispatch(sendGetQuestionnaireResponses());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const results = useSelector(selectResults);
  const [isResultsAvailable, setResultsAvailable] = useState(false);
  useEffect(() => {
    setResultsAvailable(!!results ? results.length > 0 : false);
  }, [results]);

  const studyCode = useSelector(selectStudyCode);
  const resourceData = useSelector(selectResourceData);
  const [recommendedResourceData, setRecommendedResourceData] = useState([]);
  useEffect(() => {
    setRecommendedResourceData(
      getCategoryResources(resourceData.resources, CATEGORY.recommended, studyCode),
    );
  }, [resourceData, studyCode]);

  const messages = defineMessages({
    resultsTitle: {
      id: 'results.title',
      defaultMessage: 'Results',
      description: 'Results page title text',
    },
    resultsBlurb: {
      id: 'results.blurb',
      defaultMessage:
        'Select a date to review your latest results and track your progress over time. The graph below plots your check in results from a 0 to 100 scale. Lower scores are considered better.',
      description: 'results page blurb',
    },
    resultsBlurb2: {
      id: 'results.blurb2',
      defaultMessage:
        'Please note, that this is not a diagnosis. If you need support with mood, worry, sleep, and pain, contact your healthcare team today.',
      description: 'results page blurb 2',
    },
    resultsNoCheckInsMessage: {
      id: 'results.no_check_ins',
      defaultMessage:
        'Your Check In results will be displayed here. Complete a Check In today to get started!',
      description: 'Results page message when there are no results.',
    },
    resultsCheckInButton: {
      id: 'results.check_in_button',
      defaultMessage: "Let's Go!",
      description: 'Self assessment button text',
    },
  });

  const handleOpenQuestionnaire = () => {
    navigate('/checkin', { state: { openQuestionnaire: true } });
  };

  const ResultsBlurb = ({ text }) => {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          fontWeight: FONT_WEIGHT_NORMAL,
          fontSize: '1.25rem',
          marginTop: { xs: '1.5rem', sm: '2.5rem' },
          maxWidth: '64rem',
        }}
      >
        {text}
      </Typography>
    );
  };

  return (
    <>
      <Grid container direction="column">
        <Grid item px={{ xs: '0.75rem', sm: '3.75rem' }} py={{ xs: '0.75rem', sm: '3.75rem' }}>
          <Typography
            variant="h1"
            sx={{
              color: 'text.primary',
              [theme.breakpoints.up('xs')]: {
                fontSize: FONT_SIZE_39_PX,
              },
              [theme.breakpoints.up('sm')]: {
                fontSize: '3.0625rem',
              },
              [theme.breakpoints.up('md')]: {
                fontSize: '3.8125rem',
              },
            }}
          >
            {intl.formatMessage(messages.resultsTitle)}
          </Typography>
          <ResultsBlurb text={intl.formatMessage(messages.resultsBlurb)} />
          <ResultsBlurb text={intl.formatMessage(messages.resultsBlurb2)} />
        </Grid>
        {!isResultsAvailable ? (
          <Grid item bgcolor={COLOR_DENIM} pb="3.75rem" height="100%" width="inherit">
            <AssessmentActions
              blurb={intl.formatMessage(messages.resultsNoCheckInsMessage)}
              buttonText={intl.formatMessage(messages.resultsCheckInButton)}
              onClick={handleOpenQuestionnaire}
            />
          </Grid>
        ) : (
          <Grid
            item
            bgcolor={'background.default'}
            px={{ xs: '0.75rem', sm: '3.75rem' }}
            py={{ xs: '1.25rem', sm: '3rem' }}
            width="-webkit-fill-available"
          >
            <ResultsGraphSection chartWidth={chartWidth} isMobile={isMobile} />
            <ResultsCardsSection isMobile={isMobile} />
            {!!recommendedResourceData?.length > 0 ? (
              isLargeScreen ? (
                <CategorySection
                  categoryName={CATEGORY.recommended}
                  categoryData={recommendedResourceData}
                />
              ) : (
                <CategorySectionList
                  categoryName={CATEGORY.recommended}
                  categoryData={recommendedResourceData}
                />
              )
            ) : (
              <></>
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};
